import React, { useState, useEffect } from 'react';
import { 
  Card, Form, InputNumber, Button, Table, Row, Col, Typography, 
  Select, Space, Tag, Alert, Statistic, Divider, Tabs
} from 'antd';
import { 
  CalculatorOutlined, TrophyOutlined, ArrowUpOutlined, 
  ArrowDownOutlined, DollarOutlined, InfoCircleOutlined
} from '@ant-design/icons';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer 
} from 'recharts';
import DatabaseManager from '../utils/DatabaseManager';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const InteractiveSimulator = ({ currentUser, isAdmin }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [ejecutivos, setEjecutivos] = useState([]);
  const [selectedEjecutivoId, setSelectedEjecutivoId] = useState(currentUser?.role === 'admin' ? null : currentUser?.id);
  const [medios, setMedios] = useState([]);
  const [bandasMensuales, setBandasMensuales] = useState([]);
  const [bandasTrimestrales, setBandasTrimestrales] = useState([]);
  const [parametros, setParametros] = useState({});
  const [resultados, setResultados] = useState(null);
  const [activeMode, setActiveMode] = useState('mensual'); // 'mensual' o 'trimestral'
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedTrimestre, setSelectedTrimestre] = useState(Math.ceil((new Date().getMonth() + 1) / 3));

  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1];
  const months = [
    { val: 1, name: 'Enero' }, { val: 2, name: 'Febrero' }, { val: 3, name: 'Marzo' },
    { val: 4, name: 'Abril' }, { val: 5, name: 'Mayo' }, { val: 6, name: 'Junio' },
    { val: 7, name: 'Julio' }, { val: 8, name: 'Agosto' }, { val: 9, name: 'Septiembre' },
    { val: 10, name: 'Octubre' }, { val: 11, name: 'Noviembre' }, { val: 12, name: 'Diciembre' }
  ];

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedEjecutivoId) {
      prefillFormData();
    }
  }, [selectedEjecutivoId, activeMode, selectedMonth, selectedYear, selectedTrimestre]);

  const fetchInitialData = async () => {
    try {
      const [allMedios, allBandsM, allBandsT, allParams] = await Promise.all([
        DatabaseManager.Medio.findAll(),
        DatabaseManager.BandaMensual.findAll({ where: { activo: true }, order: [['desde', 'ASC']] }),
        DatabaseManager.BandaTrimestral.findAll({ where: { activo: true }, order: [['desde', 'ASC']] }),
        DatabaseManager.Parametro.findAll()
      ]);

      setMedios(allMedios);
      setBandasMensuales(allBandsM);
      setBandasTrimestrales(allBandsT);
      
      const paramsObj = {};
      allParams.forEach(p => { paramsObj[p.nombre] = parseFloat(p.valor); });
      setParametros(paramsObj);

      if (isAdmin) {
        const allEjecs = await DatabaseManager.Ejecutivo.findAll({ where: { activo: true } });
        setEjecutivos(allEjecs);
      }
    } catch (error) {
      console.error("Error fetching simulator data:", error);
    }
  };

  const prefillFormData = async () => {
    if (!selectedEjecutivoId) return;
    setLoading(true);
    try {
      const filters = { ejecutivoId: selectedEjecutivoId, anio: selectedYear, activo: true };
      
      let pptoData = [];
      let ventaData = [];

      if (activeMode === 'mensual') {
        filters.mes = selectedMonth;
        pptoData = await DatabaseManager.Presupuesto.findAll({ where: filters });
        ventaData = await DatabaseManager.Venta.findAll({ where: filters });
      } else {
        const currentTrimestre = selectedTrimestre;
        // Para trimestral, obtenemos todos los presupuestos del año
        pptoData = await DatabaseManager.Presupuesto.findAll({ where: { ejecutivoId: selectedEjecutivoId, anio: selectedYear, activo: true } });
        // Las ventas ya vienen filtradas por trimestre en la tabla
        ventaData = await DatabaseManager.Venta.findAll({ where: { ejecutivoId: selectedEjecutivoId, anio: selectedYear, trimestre: currentTrimestre, activo: true } });
        
        const pptoAggregated = {};
        pptoData.filter(p => Math.ceil(p.mes / 3) === currentTrimestre).forEach(p => {
          pptoAggregated[p.medioId] = (pptoAggregated[p.medioId] || 0) + Number(p.presupuesto || 0);
        });

        const ventaAggregated = {};
        ventaData.forEach(v => {
          ventaAggregated[v.medioId] = (ventaAggregated[v.medioId] || 0) + Number(v.ventaReal || 0);
        });

        const newValues = {};
        medios.forEach(m => {
          newValues[`ppto_${m.id}`] = pptoAggregated[m.id] || 0;
          newValues[`venta_${m.id}`] = ventaAggregated[m.id] || 0;
        });
        form.setFieldsValue(newValues);
        setLoading(false);
        form.submit();
        return;
      }

      console.log(`[Simulator] Cargando datos para Ejecutivo ${selectedEjecutivoId}, Año ${selectedYear}, Mes ${selectedMonth}`);
      const newValues = {};
      medios.forEach(m => {
        // Sumar todos los presupuestos del mismo medio (Base + Adicionales)
        const pRows = pptoData.filter(item => item.medioId === m.id);
        const vRows = ventaData.filter(item => item.medioId === m.id);
        
        newValues[`ppto_${m.id}`] = pRows.reduce((acc, curr) => acc + Number(curr.presupuesto || 0), 0);
        newValues[`venta_${m.id}`] = vRows.reduce((acc, curr) => acc + Number(curr.ventaReal || 0), 0);
      });
      
      form.setFieldsValue(newValues);
      form.submit();
    } catch (e) {
      console.error("Error prefilling form:", e);
    } finally {
      setLoading(false);
    }
  };

  const calcularSimulacion = (values) => {
    const tasaBaseAnual = parametros['tasa_base_comision'] || 0.007;
    const pesoBolsa = activeMode === 'mensual' ? 
      (parametros['bolsa_mensual'] || 0.34) : 
      (parametros['bolsa_trimestral'] || 0.66);
    
    const bandas = activeMode === 'mensual' ? bandasMensuales : bandasTrimestrales;
    
    let totalVentas = 0;
    let totalComision = 0;
    const detalles = [];

    // Primero calculamos cumplimiento integral para ver si "puede pagar"
    let sumaPonderada = 0;
    let sumaPesos = 0;

    medios.forEach(m => {
      const ppto = values[`ppto_${m.id}`] || 0;
      const venta = values[`venta_${m.id}`] || 0;
      if (ppto > 0) {
        sumaPonderada += (venta / ppto) * m.pesoEstrategico;
        sumaPesos += m.pesoEstrategico;
      }
    });

    const cumplimientoIntegral = sumaPesos > 0 ? sumaPonderada / sumaPesos : 0;
    const compuerta = activeMode === 'mensual' ? 
      (parametros['compuerta_integral_mensual'] || 0.85) : 
      (parametros['compuerta_integral_trimestral'] || 0.9);
    
    const cumpleIntegral = cumplimientoIntegral >= compuerta;

    medios.forEach(m => {
      const ppto = values[`ppto_${m.id}`] || 0;
      const venta = values[`venta_${m.id}`] || 0;
      const cumplimiento = ppto > 0 ? (venta / ppto) : 0;
      
      // Buscar banda real
      const banda = bandas.find(b => cumplimiento >= b.desde && cumplimiento <= b.hasta) || bandas[0];
      const factor = banda?.factorIndividual || 0;

      const tasaFinal = tasaBaseAnual * pesoBolsa * factor * m.pesoEstrategico;
      const comision = venta * tasaFinal;

      totalVentas += venta;
      totalComision += comision;

      detalles.push({
        key: m.id,
        medio: m.nombre,
        ppto,
        venta,
        cumplimiento: cumplimiento * 100,
        banda: banda?.nombre || 'Fuera de rango',
        factor,
        comision,
        mensajeBanda: getMensajeBanda(cumplimiento)
      });
    });

    const ejecutivo = ejecutivos.find(e => e.id === selectedEjecutivoId) || currentUser;
    const salarioBase = ejecutivo?.salarioBase || 2000000;
    const comisionAnualizada = totalComision * (activeMode === 'mensual' ? 12 : 4);
    const pctSalario = (comisionAnualizada / (salarioBase * 12)) * 100;

    setResultados({
      totalVentas,
      totalComision,
      cumplimientoIntegral: cumplimientoIntegral * 100,
      cumpleIntegral,
      compuerta: compuerta * 100,
      pctSalario,
      detalles
    });
  };

  const getMensajeBanda = (cumplimiento) => {
    if (cumplimiento >= 1.1) return "¡Nivel Dios! ⚡ Excediste todas las metas.";
    if (cumplimiento >= 1.0) return "¡Súper Estrella! 🌟 Pasaste la meta.";
    if (cumplimiento >= 0.95) return "¡Excelente! 💎 Muy cerca del máximo.";
    if (cumplimiento >= 0.85) return "¡Buen Trabajo! 👍 Meta cumplida.";
    if (cumplimiento >= 0.7) return "¡Casi! 😅 Te faltó un poco.";
    return "Ups 📉 Hay que esforzarse más.";
  };

  return (
    <div style={{ padding: '0px' }}>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Alert
            message={`Simulador Interactivo de Comisiones (${activeMode.toUpperCase()})`}
            description={
              <div>
                <p>Usa este simulador para proyectar tus ganancias. Los cálculos usan las <strong>Bandas Reales</strong> y <strong>Parámetros</strong> configurados en el sistema.</p>
                <Space style={{ marginBottom: 15 }}>
                  <Button 
                    type={activeMode === 'mensual' ? 'primary' : 'default'} 
                    onClick={() => setActiveMode('mensual')}
                  >
                    Simulación Mensual
                  </Button>
                  <Button 
                    type={activeMode === 'trimestral' ? 'primary' : 'default'} 
                    onClick={() => setActiveMode('trimestral')}
                  >
                    Simulación Trimestral
                  </Button>
                </Space>
                <Divider style={{ margin: '10px 0' }} />
                <Space style={{ flexWrap: 'wrap' }}>
                  <Text strong>Periodo:</Text>
                  <Select value={selectedYear} style={{ width: 90 }} onChange={setSelectedYear}>
                    {[2024, 2025, 2026].map(y => <Option key={y} value={y}>{y}</Option>)}
                  </Select>
                  
                  {activeMode === 'mensual' ? (
                    <Select value={selectedMonth} style={{ width: 120 }} onChange={setSelectedMonth}>
                      {months.map(m => <Option key={m.val} value={m.val}>{m.name}</Option>)}
                    </Select>
                  ) : (
                    <Select value={selectedTrimestre} style={{ width: 110 }} onChange={setSelectedTrimestre}>
                      <Option value={1}>Trim. 1</Option>
                      <Option value={2}>Trim. 2</Option>
                      <Option value={3}>Trim. 3</Option>
                      <Option value={4}>Trim. 4</Option>
                    </Select>
                  )}

                  {isAdmin && (
                    <Select 
                      placeholder="Ver Ejecutivo" 
                      style={{ width: 220 }} 
                      value={selectedEjecutivoId}
                      onChange={setSelectedEjecutivoId}
                      allowClear
                    >
                      {ejecutivos.map(e => <Option key={e.id} value={e.id}>{e.nombre}</Option>)}
                    </Select>
                  )}
                </Space>
              </div>
            }
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
          />
        </Col>

        <Col span={8}>
          <Card title="Configuración de la Simulación" bordered={false} loading={loading}>
            <Form form={form} layout="vertical" onFinish={calcularSimulacion}>
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {isAdmin && (
                  <Form.Item label="Ejecutivo" style={{ marginBottom: 0 }}>
                    <Select 
                      placeholder="Elige un ejecutivo" 
                      onChange={setSelectedEjecutivoId}
                      value={selectedEjecutivoId}
                    >
                      {ejecutivos.map(e => (
                        <Option key={e.id} value={e.id}>{e.nombre}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}

                <Row gutter={8}>
                  <Col span={12}>
                    <Form.Item label="Mes" style={{ marginBottom: 0 }}>
                      <Select value={selectedMonth} onChange={setSelectedMonth}>
                        {months.map(m => (
                          <Option key={m.val} value={m.val}>{m.name}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Año" style={{ marginBottom: 0 }}>
                      <Select value={selectedYear} onChange={setSelectedYear}>
                        {years.map(y => (
                          <Option key={y} value={y}>{y}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </Space>

              <Divider>Metas y Ventas por Medio</Divider>

              {medios.map(m => (
                <div key={m.id} style={{ 
                  marginBottom: '16px', 
                  padding: '12px', 
                  background: '#f9f9f9', 
                  borderRadius: '8px',
                  borderLeft: `4px solid ${m.nombre.includes('TV') ? '#1890ff' : '#52c41a'}`
                }}>
                  <Text strong style={{ fontSize: '15px' }}>{m.nombre}</Text>
                  <Row gutter={8} style={{ marginTop: '8px' }}>
                    <Col span={12}>
                      <Form.Item label="Meta (Ppto)" name={`ppto_${m.id}`} style={{ marginBottom: 0 }}>
                        <InputNumber 
                          style={{ width: '100%' }} 
                          formatter={v => `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          parser={v => v.replace(/\$\s?|(,*)/g, '')}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="Venta Proyectada" name={`venta_${m.id}`} style={{ marginBottom: 0 }}>
                        <InputNumber 
                          style={{ width: '100%' }} 
                          formatter={v => `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          parser={v => v.replace(/\$\s?|(,*)/g, '')}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              ))}

              <Button 
                type="primary" 
                htmlType="submit" 
                size="large" 
                block 
                icon={<CalculatorOutlined />}
                style={{ marginTop: '10px', height: '50px', fontSize: '18px' }}
              >
                ¡Calcular Ganancias! 💰
              </Button>
            </Form>
          </Card>
        </Col>

        <Col span={16}>
          {resultados ? (
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Card style={{ background: '#e6f7ff' }}>
                    <Statistic 
                      title="Venta Total Simulada" 
                      value={resultados.totalVentas} 
                      prefix="$" 
                      precision={0} 
                    />
                  </Card>
                </Col>
                <Col span={8}>
                  <Card style={{ background: resultados.cumpleIntegral ? '#f6ffed' : '#fff1f0' }}>
                    <Statistic 
                      title="Comisión Estimada" 
                      value={resultados.totalComision} 
                      prefix="$" 
                      precision={0} 
                      valueStyle={{ color: resultados.cumpleIntegral ? '#3f8600' : '#cf1322' }}
                    />
                    {!resultados.cumpleIntegral && <Tag color="red">No cumple compuerta integral ({resultados.compuerta}%)</Tag>}
                  </Card>
                </Col>
                <Col span={8}>
                  <Card style={{ background: '#fff7e6' }}>
                    <Statistic 
                      title="% de Salario Anual" 
                      value={resultados.pctSalario} 
                      suffix="%" 
                      precision={1} 
                    />
                  </Card>
                </Col>
              </Row>

              <Card title="📊 Desglose del Cálculo (Bandas Reales)">
                <Table 
                  dataSource={resultados.detalles} 
                  pagination={false} 
                  size="small"
                  columns={[
                    { title: 'Medio', dataIndex: 'medio', key: 'medio' },
                    { title: 'Cumplimiento', dataIndex: 'cumplimiento', render: v => (
                      <Tag color={v >= 100 ? 'green' : v >= 85 ? 'blue' : 'orange'}>{v.toFixed(1)}%</Tag>
                    )},
                    { title: 'Banda Detectada', dataIndex: 'banda', key: 'banda' },
                    { title: 'Factor', dataIndex: 'factor', key: 'factor' },
                    { title: 'Comisión', dataIndex: 'comision', render: v => `$${v.toLocaleString('es-CO')}` },
                    { title: 'Explicación', dataIndex: 'mensajeBanda', key: 'mensaje' },
                  ]}
                />
              </Card>

              <Card title="📈 Comparativa de Comisiones por Medio">
                <div style={{ height: 300, width: '100%' }}>
                  <ResponsiveContainer>
                    <BarChart data={resultados.detalles}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="medio" />
                      <YAxis />
                      <RechartsTooltip formatter={v => `$${v.toLocaleString('es-CO')}`} />
                      <Legend />
                      <Bar dataKey="comision" name={`Comisión ${activeMode === 'mensual' ? 'Mes' : 'Trimestre'}`} fill="#1890ff" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </Space>
          ) : (
            <div style={{ 
              height: '100%', 
              minHeight: '500px', 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'center', 
              alignItems: 'center', 
              background: 'white',
              borderRadius: '8px',
              border: '1px dashed #d9d9d9'
            }}>
              <TrophyOutlined style={{ fontSize: '80px', color: '#faad14', marginBottom: '24px' }} />
              <Title level={2}>¿Cuánto quieres ganar hoy?</Title>
              <Paragraph style={{ fontSize: '18px', color: '#666', textAlign: 'center', maxWidth: '500px' }}>
                Selecciona un ejecutivo (si eres admin), elige el modo de simulación y ajusta las ventas para ver cómo se mueven tus premios.
              </Paragraph>
              <Button type="primary" size="large" onClick={() => form.submit()}>Iniciar Simulación con Datos Actuales</Button>
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default InteractiveSimulator;
